import { atom } from 'jotai'
import type { UniversalChart, UniversalDataPoint } from '@shared/schemas/chart'

// Use the shared UniversalChart interface as the single source of truth
export type ChartData = UniversalChart

// Factory function to create a standardized chart object
export function createStandardChartObject(
  chartSpecifics: Partial<ChartData>
): Omit<ChartData, 'id' | 'createdAt'> {
  console.log('[createStandardChartObject] Input chartSpecifics:', chartSpecifics)
  
  // Define the complete, default structure using UniversalChart
  const defaultChart: Omit<ChartData, 'id' | 'createdAt'> = {
    title: 'Untitled Chart',
    data: [],
    chartType: 'bar',
    xAxis: 'name',
    yAxis: 'value',
    configuration: {
      colorPalette: 'fireforge',
      singleColor: '#FF4500',
      responsive: true,
      maintainAspectRatio: false,
    },
    displayOptions: {
      showGridLines: true,
    },
    axisOptions: {
      xMin: null,
      xMax: null,
      yMin: null,
      yMax: null,
    },
    studies: [],
    metadata: {
      yAxisType: 'number'
    },
    savedToDatabase: false,
  };

  // Merge the provided specifics over the defaults
  const result = {
    ...defaultChart,
    ...chartSpecifics,
    // Ensure nested objects are also merged correctly
    configuration: {
      ...defaultChart.configuration,
      ...chartSpecifics.configuration,
    },
    displayOptions: {
      ...defaultChart.displayOptions,
      ...chartSpecifics.displayOptions,
    },
    axisOptions: {
      ...defaultChart.axisOptions,
      ...chartSpecifics.axisOptions,
    },
  };
  
  console.log('[createStandardChartObject] Result:', result)
  console.log('[createStandardChartObject] Result configuration:', result.configuration)
  
  return result;
}

// Atom for storing all charts
export const chartsAtom = atom<ChartData[]>([])

// Atom for the currently active chart in the chart creator
export const activeChartAtom = atom<ChartData | null>(null)

// Derived atom for chart count
export const chartCountAtom = atom((get) => get(chartsAtom).length)

// Atom for adding a new chart
export const addChartAtom = atom(
  null,
  (get, set, newChart: Omit<ChartData, 'id' | 'createdAt'>) => {
    const charts = get(chartsAtom)
    const chartWithId: ChartData = {
      ...newChart,
      id: `chart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date()
    }
    set(chartsAtom, [...charts, chartWithId])
    set(activeChartAtom, chartWithId)
    return chartWithId
  }
)

// Atom for removing a chart
export const removeChartAtom = atom(
  null,
  (get, set, chartId: string) => {
    const charts = get(chartsAtom)
    const activeChart = get(activeChartAtom)
    
    set(chartsAtom, charts.filter(chart => chart.id !== chartId))
    
    // If we're removing the active chart, clear it
    if (activeChart?.id === chartId) {
      set(activeChartAtom, null)
    }
  }
)

// Atom for updating a chart
export const updateChartAtom = atom(
  null,
  (get, set, chartId: string, updates: Partial<ChartData>) => {
    const charts = get(chartsAtom);
    const activeChart = get(activeChartAtom);
    console.log('[updateLocalChart Atom] Received for chartId:', chartId, 'Updates:', JSON.parse(JSON.stringify(updates)));
    
    const chartBeforeUpdate = charts.find(chart => chart.id === chartId);
    console.log('[updateLocalChart Atom] Chart state BEFORE update:', chartBeforeUpdate ? JSON.parse(JSON.stringify(chartBeforeUpdate)) : 'NOT FOUND');

    const updatedCharts = charts.map(chart => 
      chart.id === chartId ? { ...chart, ...updates } : chart
    );
    
    set(chartsAtom, updatedCharts);
    console.log('[updateLocalChart Atom] ChartsAtom updated. New state for chart:', 
      updatedCharts.find(chart => chart.id === chartId) ? JSON.parse(JSON.stringify(updatedCharts.find(chart => chart.id === chartId))) : 'NOT FOUND AFTER UPDATE'
    );
    
    if (activeChart?.id === chartId) {
      const newActiveChartState = { ...activeChart, ...updates };
      set(activeChartAtom, newActiveChartState);
      console.log('[updateLocalChart Atom] ActiveChart also updated:', JSON.parse(JSON.stringify(newActiveChartState)));
    }
  }
);

// Atom for clearing the active chart (for chart creator reset)
export const clearActiveChartAtom = atom(
  null,
  (_, set) => {
    set(activeChartAtom, null)
  }
) 